// components/add_portfolio_id.tsx
import React, { useState, useContext, useCallback } from 'react';
import { TextInput, Button, Alert } from '@mantine/core';
import { DocumentReference, updateDoc, Timestamp, arrayUnion, getDoc, collectionGroup, query, where, count, doc, getDocs, Firestore, limit } from 'firebase/firestore';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { MyUserDataContext } from '../contexts';
import { Service, UpdatedDataType } from '../types';
import { useFirestore } from '../helpers/firebaseContext';

interface AddPortfolioIdProps {
    serviceRef: DocumentReference;
    service: Service;
    fieldType?: 'portfolioId' | 'mainPortfolioId' | 'transferAmount';
}

interface ValidationErrors {
    fieldValue?: string;
    reason?: string;
}

const VALIDATION_RULES = {
    MIN_PORTFOLIO_ID_LENGTH: 3,
    MIN_REASON_LENGTH: 5,
} as const;

export const portfolioIDValid = async (value: string, db: Firestore, serviceRef: DocumentReference, fieldLabel?: string,) => {
    // Default validation for portfolio IDs
    if (value.length < VALIDATION_RULES.MIN_PORTFOLIO_ID_LENGTH) {
        return `${fieldLabel} must be at least ${VALIDATION_RULES.MIN_PORTFOLIO_ID_LENGTH} characters`;
    }

    //portfolio exists in portfolios collection ?
    let portfolioRef = doc(db, "portfolios", value);
    let portfoliosCollection = await getDoc(portfolioRef);

    if (portfoliosCollection.exists()) {
        return `portfolio with ID ${value} already exists`;
    }

    // check services do not have that specific portfolio ID
    let portfoliosServicesQuery = query(
        collectionGroup(db, "services"),
        where("portfolioOpeningRequest.portfolioId", "==", value),
        where("id", "!=", serviceRef.id),
        limit(1),
    );
    const portfoliosServices = await getDocs(portfoliosServicesQuery);
    if (portfoliosServices.docs.length > 0) {
        return `portfolio with ID ${value} already exists`;
    }
    return undefined;
}
const AddPortfolioId: React.FC<AddPortfolioIdProps> = ({
    serviceRef,
    service,
    fieldType = 'portfolioId'
}) => {
    const [updating, setUpdating] = useState(false);

    // Get initial value based on field type
    const initialValue = fieldType === 'mainPortfolioId'
        ? (service.portfolioOpeningRequest?.mainPortfolioId ?? '')
        : fieldType === 'transferAmount'
            ? (service.portfolioOpeningRequest?.transferAmount ?? '')
            : (service.portfolioOpeningRequest?.portfolioId ?? '');

    const [fieldValue, setFieldValue] = useState<string>(String(initialValue));
    const [reason, setReason] = useState('');
    const [errors, setErrors] = useState<ValidationErrors>({});
    const db = useFirestore();
    const userData = useContext(MyUserDataContext);

    // Dynamic labels based on field type
    const fieldLabel = fieldType === 'mainPortfolioId'
        ? 'Main Portfolio ID'
        : fieldType === 'transferAmount'
            ? 'Transfer Amount'
            : 'Portfolio ID';

    const placeholder = fieldType === 'transferAmount'
        ? 'e.g. 10000'
        : 'e.g. PORT-12345';

    const validateFieldValue = useCallback(async (value: string): Promise<string | undefined> => {
        const trimmed = value.trim();
        if (!trimmed) return `${fieldLabel} is required`;

        // Special validation for transfer amount
        if (fieldType === 'transferAmount') {
            const numValue = Number(trimmed);
            if (isNaN(numValue)) return 'Transfer amount must be a valid number';
            if (!Number.isFinite(numValue)) return 'Transfer amount must be a finite number';
            if (numValue <= 0) return 'Transfer amount must be greater than 0';
            return undefined;
        }
        if (fieldType === "portfolioId") {
            return await portfolioIDValid(trimmed, db, serviceRef, fieldLabel);
        }

        return undefined;
    }, [fieldLabel, fieldType]);

    const validateReason = useCallback((value: string): string | undefined => {
        const trimmed = value.trim();
        if (!trimmed) return "Reason is required";
        if (trimmed.length < VALIDATION_RULES.MIN_REASON_LENGTH) {
            return `Reason must be at least ${VALIDATION_RULES.MIN_REASON_LENGTH} characters`;
        }
        return undefined;
    }, []);

    const validateForm = useCallback(async (): Promise<boolean> => {
        const fieldValueError = await validateFieldValue(fieldValue);
        const reasonError = validateReason(reason);

        const newErrors: ValidationErrors = {
            fieldValue: fieldValueError,
            reason: reasonError,
        };

        setErrors(newErrors);
        return !fieldValueError && !reasonError;
    }, [fieldValue, reason, validateFieldValue, validateReason]);

    const isFormValid = useCallback((): boolean => {
        // For transfer amount, check if it's a valid positive number
        if (fieldType === 'transferAmount') {
            const numValue = Number(fieldValue.trim());
            return Number.isFinite(numValue) &&
                numValue > 0 &&
                reason.trim().length >= VALIDATION_RULES.MIN_REASON_LENGTH;
        }

        // For portfolio IDs
        return fieldValue.trim().length >= VALIDATION_RULES.MIN_PORTFOLIO_ID_LENGTH &&
            reason.trim().length >= VALIDATION_RULES.MIN_REASON_LENGTH;
    }, [fieldValue, reason, fieldType]);

    const handleFieldValueChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.currentTarget.value;
        setFieldValue(value);

        // Clear error if input becomes valid
        const error = await validateFieldValue(value);
        if (!error && errors.fieldValue) {
            setErrors(prev => ({ ...prev, fieldValue: undefined }));
        }
    }, [errors.fieldValue, validateFieldValue]);

    const handleFieldValueBlur = useCallback(async () => {
        const error = await validateFieldValue(fieldValue);
        if (error) {
            setErrors(prev => ({ ...prev, fieldValue: error }));
        }
    }, [fieldValue, validateFieldValue]);

    const handleReasonChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.currentTarget.value;
        setReason(value);

        if (value.trim().length >= VALIDATION_RULES.MIN_REASON_LENGTH && errors.reason) {
            setErrors(prev => ({ ...prev, reason: undefined }));
        }
    }, [errors.reason]);

    const handleReasonBlur = useCallback(() => {
        const error = validateReason(reason);
        if (error) {
            setErrors(prev => ({ ...prev, reason: error }));
        }
    }, [reason, validateReason]);

    const handleSubmit = useCallback(async () => {
        if (!(await validateForm())) return;

        setUpdating(true);

        try {
            const trimmedValue = fieldValue.trim();

            // Get initial value for update tracking
            const initialFieldValue = fieldType === 'mainPortfolioId'
                ? (service.portfolioOpeningRequest?.mainPortfolioId || "")
                : fieldType === 'transferAmount'
                    ? (service.portfolioOpeningRequest?.transferAmount || "")
                    : (service.portfolioOpeningRequest?.portfolioId || "");

            const newEntry: UpdatedDataType = {
                updated_by: userData.user?.email || "unknown",
                updated_on: Timestamp.now(),
                updated_field_name: [fieldType],
                updated_fields: [{ [fieldType]: trimmedValue }],
                initial_value: [{ [fieldType]: String(initialFieldValue) }],
                reason: reason.trim()
            };

            // Convert to number for transferAmount field with strict validation
            let finalFieldValue: string | number = trimmedValue;

            if (fieldType === 'transferAmount') {
                const numericValue = Number(trimmedValue);

                if (!Number.isFinite(numericValue) || numericValue <= 0) {
                    // Fallback validation to prevent invalid numeric values from being stored
                    setErrors((prev) => ({
                        ...prev,
                        fieldValue: 'Transfer amount must be a positive number',
                    }));
                    setUpdating(false);
                    return;
                }

                finalFieldValue = numericValue;
            }

            // Update the appropriate field
            const portfolioOpeningRequest = {
                ...service.portfolioOpeningRequest,
                [fieldType]: finalFieldValue
            };

            await updateDoc(serviceRef, {
                portfolioOpeningRequest,
                updated_data: arrayUnion(newEntry)
            });

            notifications.show({
                title: 'Success!',
                message: `${fieldLabel} updated successfully`,
                color: 'green',
                icon: <IconCheck size={16} />
            });

            setFieldValue('');
            setReason('');
            setErrors({});
            modals.closeAll();

        } catch (error) {
            console.error(`Error updating ${fieldType}:`, error);

            const message = error instanceof Error ? error.message : 'Unknown error occurred';
            notifications.show({
                title: 'Error',
                message: `Failed to update ${fieldLabel}: ${message}`,
                color: 'red',
                icon: <IconAlertCircle size={16} />
            });
        } finally {
            setUpdating(false);
        }
    }, [validateForm, userData.user?.email, fieldValue, reason, service.portfolioOpeningRequest, serviceRef, fieldType, fieldLabel]);

    const hasErrors = Boolean(errors.fieldValue || errors.reason);
    const formStatus = isFormValid();

    return (
        <>
            <TextInput
                label={`Enter ${fieldLabel}`}
                data-autofocus
                value={fieldValue}
                onChange={handleFieldValueChange}
                onBlur={handleFieldValueBlur}
                error={errors.fieldValue}
                placeholder={placeholder}
                required
            />

            <TextInput
                label="Reason for Update"
                value={reason}
                onChange={handleReasonChange}
                onBlur={handleReasonBlur}
                error={errors.reason}
                placeholder={`Please provide a detailed reason (minimum ${VALIDATION_RULES.MIN_REASON_LENGTH} characters)...`}
                required
            />

            <Button
                loading={updating}
                fullWidth
                disabled={updating}
                onClick={handleSubmit}
                mt="md"
            >
                Submit
            </Button>

            {hasErrors && (
                <Alert
                    icon={<IconAlertCircle size={16} />}
                    title="Please fix the following errors:"
                    color="red"
                    mt="sm"
                >
                    {errors.fieldValue && <div>• {errors.fieldValue}</div>}
                    {errors.reason && <div>• {errors.reason}</div>}
                </Alert>
            )}

            {!updating && (
                <Alert
                    color={formStatus ? 'green' : 'gray'}
                    variant="light"
                    mt="xs"
                    style={{ fontSize: '12px' }}
                >
                    Form is {formStatus ? 'ready to submit' : 'incomplete'}
                </Alert>
            )}
        </>
    );
};

export default AddPortfolioId;